import React from "react";
import { Link } from "react-router-dom";
import {
  HERO,
  HERO_LEGACY_SUB_LINES,
  CASE_STUDIES,
  DIFFERENTIATORS,
  AWARDS,
} from "../data/content";
import {
  Reveal,
  Tag,
  RedDot,
  ArrowLink,
  CredentialPill,
  SectionNum,
} from "../components/ui-primitives";
import HeroStatsTabs from "../components/HeroStatsTabs";
import HeroTestimonialSlider from "../components/HeroTestimonialSlider";
import HeroCapabilitiesTabs from "../components/HeroCapabilitiesTabs";
import AwardsTicker from "../components/AwardsTicker";
import SubstackFeed from "../components/SubstackFeed";
import ClosingCTA from "../components/ClosingCTA";

export default function HomePage() {
  return (
    <main data-testid="home-page">
      {/* HERO — NAVY MONOLITH */}
      <section
        data-testid="hero-section"
        style={{ background: "var(--color-primary)", color: "var(--color-on-primary)" }}
      >
        <div className="di-container" style={{ paddingTop: 104, paddingBottom: 32 }}>
          <Reveal>
            <div className="flex flex-wrap items-center gap-3 mb-10 fade-up">
              {HERO.preTag.map((t, i) => (
                <React.Fragment key={t}>
                  <span
                    className="label-sm"
                    style={{ color: "var(--color-tertiary)", fontWeight: 600, letterSpacing: "0.12em" }}
                  >
                    {t}
                  </span>
                  {i < HERO.preTag.length - 1 && (
                    <span
                      style={{
                        width: 3,
                        height: 3,
                        borderRadius: "50%",
                        background: "var(--color-tertiary)",
                        display: "inline-block",
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </Reveal>

          <h1
            className="display-lg fade-up-delay-1"
            style={{
              color: "var(--color-on-primary)",
              fontSize: "clamp(2.75rem, 7vw, 5.5rem)",
              maxWidth: 1100,
              marginBottom: 28,
            }}
          >
            Brands,
            <br />
            reach Bharat.
          </h1>

          <p
            className="body-lg fade-up-delay-2"
            style={{
              color: "rgba(249,249,248,0.7)",
              maxWidth: "100%",
              marginBottom: 0,
              fontSize: "clamp(0.875rem, 0.55rem + 0.75vw, 1.1875rem)",
              lineHeight: 1.55,
            }}
          >
            {HERO_LEGACY_SUB_LINES.map((line, lineIdx) => {
              // Stable key per subline derived from its first fragment text
              // (HERO_LEGACY_SUB_LINES is module-level static, never reordered,
              // so this is unique and reconciliation-safe).
              const lineKey = line[0]?.text || `subline-${lineIdx}`;
              return (
                <span key={lineKey} className="hero-subline">
                  {line.map((frag) =>
                    frag.hl ? (
                      <span
                        key={`hl-${frag.text}`}
                        data-testid="hero-subline-highlight"
                        style={{
                          color: "rgba(249,249,248,1)",
                          fontWeight: 600,
                        }}
                      >
                        {frag.text}
                      </span>
                    ) : (
                      <React.Fragment key={`tx-${frag.text}`}>
                        {frag.text}
                      </React.Fragment>
                    )
                  )}
                </span>
              );
            })}
          </p>

          {/* Single hero CTA — Talk to us. */}
          <div className="mt-8 fade-up-delay-3">
            <Link
              to="/contact"
              data-testid="hero-cta-talk"
              className="btn btn-accent inline-flex items-center gap-2"
            >
              Talk to us
              <span aria-hidden>→</span>
            </Link>
          </div>
        </div>

        {/* Capabilities band — inside the hero, full-bleed.
            Single top border = "sub-section separator within same surface".
            This rule is used ONLY here on the page; everywhere else, sections
            are separated by surface-color changes. Discipline by exception. */}
        <div
          className="fade-up-delay-3"
          style={{
            borderTop: "1px solid rgba(249,249,248,0.12)",
            overflow: "hidden",
            paddingTop: 28,
            paddingBottom: 20,
          }}
        >
          <HeroCapabilitiesTabs />

          {/* Quiet text link to the Services depth page — replaces the
              old "Every Channel. One Partner." block's navigation role. */}
          <div className="di-container" style={{ paddingTop: 18 }}>
            <Link
              to="/services"
              data-testid="hero-see-capabilities"
              className="label-md inline-flex items-center gap-2"
              style={{
                color: "rgba(249,249,248,0.7)",
                letterSpacing: "0.12em",
                fontWeight: 600,
                textDecoration: "none",
                paddingBottom: 2,
                borderBottom: "1px solid rgba(249,249,248,0.25)",
                transition: "color 0.2s ease, border-color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "rgba(249,249,248,1)";
                e.currentTarget.style.borderColor = "var(--color-tertiary)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "rgba(249,249,248,0.7)";
                e.currentTarget.style.borderColor = "rgba(249,249,248,0.25)";
              }}
            >
              See our full capabilities
              <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* PROOF: Stats tabs + ticker — own light section */}
      <section
        data-testid="proof-section"
        style={{
          background: "var(--color-surface-container-low)",
          paddingTop: "var(--space-40)",
          paddingBottom: "var(--space-40)",
        }}
      >
        <div className="di-container">
          <HeroStatsTabs theme="light" />
        </div>
      </section>

      {/* BRAND LOVE — separate, slightly different surface */}
      <section
        data-testid="brand-love-section"
        style={{
          background: "var(--color-surface-container)",
          paddingTop: "var(--space-40)",
          paddingBottom: "var(--space-56)",
        }}
      >
        <div className="di-container">
          <HeroTestimonialSlider theme="light" />
        </div>
      </section>

      {/* CASE STUDIES */}
      <section
        id="work"
        data-testid="work-section"
        className="di-section"
        style={{ background: "var(--color-surface-container-low)" }}
      >
        <div className="di-container">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <Tag>Selected Work</Tag>
              <h2 className="display-md mt-6">
                Campaigns that moved the needle.
              </h2>
            </div>
            <ArrowLink to="/work" data-testid="work-see-all">See all our work</ArrowLink>
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-12"
            style={{ background: "var(--color-surface-container)", gap: "1px" }}
          >
            {CASE_STUDIES.map((c, i) => (
              <Reveal
                key={c.sector}
                delay={i * 80}
                className={`${
                  i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : "md:col-span-12"
                }`}
              >
                <div
                  style={{
                    background: "var(--color-surface-container-lowest)",
                    padding: "var(--space-48)",
                    height: "100%",
                  }}
                >
                  <div className="flex items-center justify-between mb-8">
                    <span className="label-sm" style={{ color: "var(--color-tertiary)" }}>
                      {c.sector}
                    </span>
                    <SectionNum>{String(i + 1).padStart(2, "0")}</SectionNum>
                  </div>
                  <h3
                    className="display-sm"
                    style={{ marginBottom: 16, whiteSpace: "pre-line", color: "var(--color-primary)" }}
                  >
                    {c.headline}
                  </h3>
                  <p className="body-md" style={{ color: "var(--color-on-surface-variant)", marginBottom: 24 }}>
                    {c.body}
                  </p>
                  <div
                    className="flex flex-wrap gap-2 pt-6"
                    style={{ borderTop: "1px solid var(--color-outline)" }}
                  >
                    {c.stats.map((st) => (
                      <CredentialPill key={st}>{st}</CredentialPill>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY DESIGNINDYA — navy section */}
      <section
        id="why"
        data-testid="why-section"
        className="di-section"
        style={{ background: "var(--color-primary)", color: "var(--color-on-primary)" }}
      >
        <div className="di-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Tag light>Why DesignIndya</Tag>
              <h2 className="display-md mt-6" style={{ color: "var(--color-on-primary)" }}>
                We get you rates
                <br />
                you can't get yourself.
              </h2>
              <p
                className="mt-8"
                style={{ color: "rgba(249,249,248,0.75)", maxWidth: 520, fontSize: "clamp(0.9375rem, 0.6rem + 0.6vw, 1.125rem)", lineHeight: 1.55 }}
              >
                <span className="editorial-line">We buy for 150+ clients at once.</span>
                <span className="editorial-line">Volume no advertiser can match —</span>
                <span className="editorial-line">and rates the rate card never shows.</span>
              </p>
              <div className="mt-10">
                <Link
                  to="/about"
                  data-testid="why-about-link"
                  className="label-md"
                  style={{
                    color: "var(--color-on-primary)",
                    paddingBottom: 4,
                    borderBottom: "1.5px solid rgba(249,249,248,0.4)",
                    display: "inline-flex",
                    gap: 8,
                  }}
                >
                  Our Story →
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6 lg:col-start-7">
              <ul className="space-y-0">
                {DIFFERENTIATORS.map((d, i) => (
                  <Reveal key={d.n} delay={i * 100}>
                    <li
                      className="grid grid-cols-12 gap-6 py-8"
                      style={{
                        borderTop: i === 0 ? "1px solid rgba(249,249,248,0.12)" : "none",
                        borderBottom: "1px solid rgba(249,249,248,0.12)",
                      }}
                    >
                      <div className="col-span-2">
                        <div
                          className="display-sm tabular"
                          style={{ color: "var(--color-tertiary)", fontWeight: 800, fontSize: "1.75rem" }}
                        >
                          {d.n}
                        </div>
                      </div>
                      <div className="col-span-10">
                        <h4 className="headline-md" style={{ color: "var(--color-on-primary)", marginBottom: 12 }}>
                          {d.title}
                        </h4>
                        <p className="body-md" style={{ color: "rgba(249,249,248,0.7)" }}>
                          {d.body}
                        </p>
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* AWARDS & RECOGNITION — same vertical rhythm as the BY THE NUMBERS
          ticker section above (proof-section). The section pads 80/40 instead
          of using di-section's 80/80 because the AwardsTicker handles its own
          internal breathing; doubling-up via di-section creates a 190px+ gap
          to the next section. */}
      <section
        data-testid="credentials-section"
        style={{
          background: "var(--color-surface)",
          paddingTop: "var(--space-80)",
          paddingBottom: "var(--space-40)",
        }}
      >
        <div className="di-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-7">
              <Tag>Awards & Recognition</Tag>
              <h2 className="display-md mt-6">
                Recognised by the media houses we work with.
              </h2>
            </div>
          </div>
        </div>

        {/* Awards ticker — full-bleed marquee, coherent with the WHAT WE DO and
            BY THE NUMBERS tickers (escapes the parent .di-container, span the
            viewport edge-to-edge). */}
        <AwardsTicker />
      </section>

      {/* INSIGHTS */}
      <section
        id="insights"
        data-testid="insights-section"
        className="di-section"
        style={{ background: "var(--color-surface-container-low)" }}
      >
        <div className="di-container">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <Tag>Insights</Tag>
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-4">
                <h2 className="display-md" style={{ margin: 0 }}>
                  News &amp; Thought Leadership
                </h2>
                <a
                  href="https://designindya.substack.com"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="insights-substack-link"
                  className="label-md"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 20px",
                    borderRadius: "var(--radius-pill)",
                    background: "var(--color-tertiary)",
                    color: "var(--color-on-tertiary)",
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    border: "1px solid var(--color-tertiary)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Subscribe on Substack ↗
                </a>
              </div>
            </div>
          </div>

          <SubstackFeed />
        </div>
      </section>

      {/* CLOSING CTA — navy. Shared component, repeats on every page except Investors. */}
      <ClosingCTA />
    </main>
  );
}
